//Library Imports
import { Row } from "antd";
import React from "react";
import { FC, Suspense, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import store, { RootState } from "store";

//Local Imports
import { ANALYTICS_ACCOUNTS_DASHBOARD } from "constants/types";
import {
  analyticsDatesFilterChange,
  resetSelectedCampaigns,
  resetSelectedEmails,
  setCampaignsFilter,
} from "modules/Analytics/Dashboard/actions";
import SearchField from "modules/Analytics/Shared/SearchField";
import { getQuarterDates, getQueryAccountId, removeAllCampaignsFromQueryParams } from "utils";
import { getAccountsDashboard, getAccountsDetails, resetAccountDashboard } from "../actions";
import Send from "../../../../assets/img/icons/Send.svg";
import ExportAnalyticsPopup from "modules/Analytics/Shared/ExportAnalyticsPopup";
import ButtonRightIcon from "modules/Common/Components/ButtonRightIcon";
import { getAllAnalyticsTab, getConstantCampaignEmailId } from "modules/Analytics/utils";
import { ANALYTICS_DEFAULT_DATES_FILTER } from "modules/Analytics/Contacts/utils";

//CSS Imports
import styled from "styled-components";
import { getQueryAllCampaigns } from "modules/EmailBrowser/utils";

interface IAccountsDashboard {
  page?: string;
  tab?: string;
  setSearchedText: (value: string) => void;
  searchedText: string;
}

const AccountContactsListing = React.lazy(() => import("./AccountContactsListing"));
const AccountStatsBar = React.lazy(() => import("./AccountStatsBar"));
const DEFAULT_PAGE_LIMIT = 20;

const AccountsDashboard: FC<IAccountsDashboard> = (props: IAccountsDashboard) => {
  const { setSearchedText, searchedText } = props;

  //Local States
  const [firstTime, setFirstTime] = useState(true);

  //Redux States
  const stats = useSelector((state: RootState) => state.accountsDashboard.data.account_stats);
  const disableExportButton = useSelector((state: RootState) => state.analyticsDashboard.exportLoading);
  const loading = useSelector((state: RootState) => state.accountsDashboard.loading);
  const loadingDasboard = useSelector((state: RootState) => state.accountsDashboard.loadingDasboard);
  const isContactsOnly = useSelector((state: RootState) => state.accountsDashboard.contactsOnly);
  const contacts = useSelector((state: RootState) => state.accountsDashboard.data.account_contacts);
  const AllCampaignsFilterCheck = useSelector(
    (state: RootState) => state.analyticsDashboard.campaignsFilters.isAllCampaigns
  );
  const { isShareProfile, isDailyIQ, isWeeklyAccountInsights } = useSelector(
    (state: RootState) => state.analyticsDashboard.appliedCampaignsFilters
  );
  const selectedAccount = useSelector((state: RootState) => state.analyticsAccounts.selectedAccount);

  const getPagination = (resetPagination?: any, contactsOnly?: boolean, params?: any, resetSearch?: boolean) => {
    let analyticsDashboard = store.getState().analyticsDashboard;
    let analyticsGraph = store.getState().analyticsGraph;
    let { appliedCampaignIds, appliedAssetsIds, excludeCampaignsIds, excludeAssetsIds } = analyticsDashboard;
    let { startDate, endDate } = analyticsGraph;
    if (resetPagination) store.dispatch({ type: ANALYTICS_ACCOUNTS_DASHBOARD.RESET, payload: true });
    if (startDate === "" && endDate === "") {
      analyticsDatesFilterChange(ANALYTICS_DEFAULT_DATES_FILTER);
      startDate = getQuarterDates().start_date;
      endDate = getQuarterDates().end_date;
    }

    // to update emails ids based on filters we need to clone the arrays
    let selected_emails_ids: number[] | string[] = [...appliedAssetsIds];
    let selected_campaings_ids: number[] | string[] = [...appliedCampaignIds];

    // check if is share profile || daily IQ || weekly accounts insights
    if (isShareProfile || isDailyIQ || isWeeklyAccountInsights) {
      let campaign_email_id = getConstantCampaignEmailId({ isShareProfile, isDailyIQ, isWeeklyAccountInsights });
      selected_campaings_ids = [campaign_email_id];
      selected_emails_ids = [campaign_email_id];
    }
    const AllCampaignsFilter = store.getState().analyticsDashboard.appliedCampaignsFilters.isAllCampaigns;
    let _params: any = {
      allCampaigns: AllCampaignsFilter,
      limit: DEFAULT_PAGE_LIMIT,
      accountId: getQueryAccountId(),
      offset: resetPagination ? 0 : contacts?.length,
      searchWord: resetSearch === true ? "" : searchedText,
      onlyContacts: contactsOnly ? 1 : 0,
      emailIds: selected_emails_ids,
      startDate: startDate,
      endDate: endDate,
      campaignIds: selected_campaings_ids,
    };

    if (AllCampaignsFilter) {
      _params["excludeCampaignsIds"] = excludeCampaignsIds;
      _params["excludeAssetsIds"] = excludeAssetsIds;
    }

    if (params !== undefined && params !== null) {
      _params["sortType"] = params.sortType;
      _params["sortBy"] = params.sortBy;
    }
    return _params;
  };

  const getAccountDetailsParams = () => {
    let analyticsDashboard = store.getState().analyticsDashboard;
    let analyticsGraph = store.getState().analyticsGraph;
    let { appliedCampaignIds, appliedAssetsIds, excludeCampaignsIds, excludeAssetsIds } = analyticsDashboard;
    let { startDate, endDate } = analyticsGraph;
    if (startDate === "" && endDate === "") {
      analyticsDatesFilterChange(ANALYTICS_DEFAULT_DATES_FILTER);
      startDate = getQuarterDates().start_date;
      endDate = getQuarterDates().end_date;
    }
    // to update emails ids based on filters we need to clone the arrays
    let selected_emails_ids: number[] | string[] = [...appliedAssetsIds];
    let selected_campaings_ids: number[] | string[] = [...appliedCampaignIds];

    // check if is share profile || daily IQ || weekly accounts insights
    if (isShareProfile || isDailyIQ || isWeeklyAccountInsights) {
      let campaign_email_id = getConstantCampaignEmailId({ isShareProfile, isDailyIQ, isWeeklyAccountInsights });
      selected_campaings_ids = [campaign_email_id];
      selected_emails_ids = [campaign_email_id];
    }

    const AllCampaignsFilter = store.getState().analyticsDashboard.appliedCampaignsFilters.isAllCampaigns;
    let _params: any = {
      allCampaigns: AllCampaignsFilter,
      account_id: getQueryAccountId(),
      email_ids: selected_emails_ids,
      start_date: startDate,
      end_date: endDate,
      campaign_ids: selected_campaings_ids,
    };
    if (AllCampaignsFilter) {
      _params["excludeCampaignsIds"] = excludeCampaignsIds;
      _params["excludeAssetsIds"] = excludeAssetsIds;
    }
    return _params;
  };

  useEffect(() => {
    if (!firstTime) {
      getAccountsDashboard(getPagination(true, false), true);
    }
  }, [searchedText]);

  useEffect(() => {
    const isAllCampaigns = getQueryAllCampaigns();
    if (isAllCampaigns === "true") {
      setCampaignsFilter({ isAllCampaigns: true });
      analyticsDatesFilterChange({ value: "all", label: "All time" });
      resetSelectedCampaigns();
      resetSelectedEmails();
    }
    window.scrollTo(0, 0);
    if (stats === undefined || Object.keys(stats).length <= 0) {
      getAccountsDashboard(getPagination(true, false, null, true), true, setFirstTime);
      getAccountsDetails(getAccountDetailsParams());
    } else setFirstTime(false);
    setSearchedText("");

    return () => {
      removeAllCampaignsFromQueryParams();
      resetAccountDashboard(false);
    };
  }, []);

  return (
    <Wrapper>
      <Row gutter={20}>
        <Suspense
          fallback={<></>}
          children={<AccountStatsBar loading={loadingDasboard} isContactsOnly={isContactsOnly} />}
        />
      </Row>
      <Row className="justify-content-between pb-3">
        {loadingDasboard ? (
          <div className="account-heading"></div>
        ) : (
          <div className="account-heading">{selectedAccount} Contacts:</div>
        )}

        <ul className="ul-parent">
          <li className={``}>
            <div className="scaled-width-35">
              <SearchField placeholder="Search" handleSearch={setSearchedText} reset={searchedText}></SearchField>
            </div>
          </li>
          <li>
            <ul className="ul-child">
              <li className={`right`}>
                <ButtonRightIcon
                  label="Export"
                  onClick={() => getAllAnalyticsTab("accounts_dashboard", searchedText)}
                  icon={Send}
                  outlineClass="auto-width"
                  disable={disableExportButton}
                />
              </li>
            </ul>
          </li>
        </ul>
      </Row>
      <Row className="mb-5">
        <Suspense
          fallback={<></>}
          children={
            <AccountContactsListing searchedText={searchedText} getPagination={getPagination} loading={loading} />
          }
        />
      </Row>
      <ExportAnalyticsPopup page={"accounts_dashboard"} searchedText={searchedText} />
    </Wrapper>
  );
};

export default AccountsDashboard;

const Wrapper = styled.div`
  .scaled-width-35 {
    width: 200px;
  }
  .account-heading {
    font-size: 24px;
    color: #424b50;
    font-weight: 400;
    font-family: "Roboto Slab";
    display: flex;
    align-items: center;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 16px;
    }
  }
  ul {
    list-style: none;
  }
  .ul-parent {
    display: flex;
  }
`;
